import { useEffect, useState } from 'react';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from 'services/medusa/config';
import { useCustomer } from '../context/CustomerContext';

// Ofertas activas por producto (backend: GET /store/promo-2x1): 2x1, 3x2 o
// % de descuento. Un producto puede tener a la vez un "compra y llévate"
// (2x1/3x2) y un % de descuento. Una sola petición por carga de página,
// compartida por todas las tarjetas. El descuento real lo calcula Medusa en el
// carrito; esto solo pinta los badges y el precio rebajado.
// Público → ofertas públicas; distribuidora VIP → las VIP; distribuidora → ninguna.
//   productIds / variantIds → productos y variantes con cualquier oferta
//   ofertas[productId]      → lista de { tipo, porcentaje, variant_ids }
const NONE = { productIds: new Set(), variantIds: new Set(), ofertas: {} };
const EMPTY = { publico: NONE, vip: NONE };
// Cache con vida corta: dedupe entre tarjetas en una misma vista, pero refresca
// solo (sin recarga dura) para reflejar cambios hechos en el admin.
const CACHE_TTL = 30_000;
let cache = null;
let cacheAt = 0;
let pending = null;

function load() {
  if (cache && Date.now() - cacheAt < CACHE_TTL) return Promise.resolve(cache);
  if (!pending) {
    pending = fetch(`${MEDUSA_API_URL}/store/promo-2x1`, {
      headers: { 'x-publishable-api-key': MEDUSA_API_KEY },
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => {
        const pack = (x = {}) => {
          // Normaliza a lista: el backend nuevo manda ofertas[pid] = [oferta, ...];
          // el anterior mandaba una sola oferta (o nada: solo 2x1 por variantes).
          const ofertas = {};
          for (const [pid, v] of Object.entries(x.ofertas || {})) {
            ofertas[pid] = Array.isArray(v) ? v : [v];
          }
          const todas = Object.values(ofertas).flat();
          return {
            productIds: new Set([...(x.product_ids || []), ...Object.keys(ofertas)]),
            variantIds: new Set([...(x.variant_ids || []), ...todas.flatMap((o) => o.variant_ids || [])]),
            ofertas,
          };
        };
        cache = { publico: pack(d), vip: pack(d.vip) };
        cacheAt = Date.now();
        return cache;
      })
      .catch(() => EMPTY)
      .finally(() => { pending = null; }); // permite refrescar al vencer el TTL
  }
  return pending;
}

export function usePromo2x1() {
  const { isDistributor, isVip } = useCustomer();
  const [promo, setPromo] = useState(cache || EMPTY);
  useEffect(() => {
    let alive = true;
    load().then((p) => alive && setPromo(p));
    return () => { alive = false; };
  }, []);
  if (isVip) return promo.vip;
  if (isDistributor) return NONE;
  return promo.publico;
}

// De la lista de ofertas de un producto (ofertas[productId]):
//   buyGetDe  → el "compra y llévate" (2x1 o 3x2), o null
//   descuentoDe → el % de descuento, o null
export function buyGetDe(ofertas) {
  return (ofertas || []).find((o) => o.tipo === '2x1' || o.tipo === '3x2') || null;
}
export function descuentoDe(ofertas) {
  return (ofertas || []).find((o) => o.tipo === 'descuento') || null;
}

// Etiqueta corta de una oferta: "2x1", "3x2" o "-20%".
export function etiquetaOferta(oferta) {
  if (!oferta) return '';
  return oferta.tipo === 'descuento' ? `-${oferta.porcentaje}%` : oferta.tipo;
}

// Texto para la ficha del producto.
export function textoOferta(oferta) {
  if (!oferta) return '';
  if (oferta.tipo === '2x1') return 'Llévate 2 y paga 1 — se aplica solo en tu carrito.';
  if (oferta.tipo === '3x2') return 'Compra 2 y llévate 3 — se aplica solo en tu carrito.';
  return `${oferta.porcentaje}% de descuento — se aplica solo en tu carrito.`;
}

// Precio con el % aplicado (solo para mostrarlo; Medusa calcula el real).
export function precioConDescuento(precio, oferta, variantId) {
  if (oferta?.tipo !== 'descuento' || !oferta.porcentaje) return null;
  if (variantId && oferta.variant_ids?.length && !oferta.variant_ids.includes(variantId)) return null;
  return Math.round(Number(precio) * (100 - oferta.porcentaje)) / 100;
}
